<template>
  <section>
    <div
      class="app"
      id="app"
      :class="{ 'inter-font': isInterArea, 'admin-area': isAdminArea, 'admin-area--no-navbar': isAdminArea && hideNavbar }"
    >
      <navbar
        v-if="!shouldShowVendorSidebar && loaded && !$route.meta.ghTheme"
        class="app-navbar"
        :isSignedIn="isSignedIn"
        :class="{ 'is-invisible navbar--hidden': hideNavbar }"
      />

      <!-- <sw-update-prompt /> -->

      <main class="layout" v-if="shouldShowVendorSidebar">
        <VendorSidebar />

        <router-view
          :key="$route.params.id"
          class="app-page"
        />
      </main>

      <router-view
        v-else
        :key="$route.params.id"
        class="app-page"
      />
    </div>

    <Footer v-if="loaded && !hideFooter && !$route.meta.ghTheme" />
  </section>
</template>

<script>
import navbar from "./components/elements/navbar";
import Footer from "./components/elements/Footer.vue";

import "buefy/dist/buefy.css";

import { mapState, mapActions } from "vuex";

import "../node_modules/@fortawesome/fontawesome-free/js/all.js";

import {
  routesToHideFooter,
  routesToHideFooterAndNavBar,
} from "./enums/RouteNames";

import { ResellerRoles } from "./enums/ResellerRoles";
import {
  UserActions,
  UsersStates,
} from "./store/Storetypes";

import AOS from "aos";
import "aos/dist/aos.css";

import VendorSidebar from "./Company/VendorSidebar.vue";
import { HelpersMixin } from "./mixins";
import { LOCALES, DEFAULT_LOCALE, localePath, splitLocalePath, currentLocaleInfo } from "./i18n";

const DEFAULT_TITLE = "Guiders";
const SITE_URL = "https://guiders.pt";
const SITE_NAME = "Guiders.pt";
const DEFAULT_DESCRIPTION =
  "Private guided tours, tuk tuk tours and the Lisbon Visitor Pass, led by licensed local guides across Portugal.";
const DEFAULT_IMAGE = `${SITE_URL}/img/share/guiders-portugal.webp`;

// Who we are, for search engines and AI assistants. Added to every page.
const ORGANIZATION_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "TravelAgency",
      "@id": `${SITE_URL}/#organization`,
      name: SITE_NAME,
      url: SITE_URL,
      logo: `${SITE_URL}/android-chrome-512x512.png`,
      image: DEFAULT_IMAGE,
      description: DEFAULT_DESCRIPTION,
      email: "support@guiders.pt",
      telephone: "+351920043375",
      address: { "@type": "PostalAddress", addressLocality: "Lisbon", addressCountry: "PT" },
      areaServed: { "@type": "Country", name: "Portugal" },
      // Turismo de Portugal tourism-activity licence (RNAAT)
      identifier: { "@type": "PropertyValue", propertyID: "RNAAT", value: "61/2023" },
      // Profiles that are the same business: helps Google link the brand to
      // its reviews. Add the Google Business Profile link here once it exists.
      sameAs: [
        "https://www.instagram.com/guiders.pt/",
        "https://www.facebook.com/guiders.pt",
        "https://www.tiktok.com/@guiders.pt",
        "https://www.tripadvisor.com/Attraction_Review-g189158-d26717876-Reviews-Guiders_pt_Shared_Tours_Activities-Lisbon_Lisbon_District_Central_Portugal.html",
      ],
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: SITE_URL,
      name: SITE_NAME,
      publisher: { "@id": `${SITE_URL}/#organization` },
      inLanguage: "en",
    },
  ],
};

export default {
  name: "App",

  mixins: [HelpersMixin],

  // Site-wide defaults built from the route's meta (title, description,
  // canonical, robots, og). Pages with their own metaInfo override any tag
  // that uses the same vmid, so every page gets one complete set of tags.
  metaInfo() {
    const route = this.$route;
    const meta = route.meta || {};
    const resolve = (value) =>
      typeof value === "function" ? value(route) : value;

    const title = resolve(meta.title) || DEFAULT_TITLE;
    const description = resolve(meta.description) || DEFAULT_DESCRIPTION;
    // Canonical never carries query strings (referral, fbclid, utm...)
    const pathUrl = `${SITE_URL}${route.path === "/" ? "/" : route.path.replace(/\/+$/, "")}`;
    // Translated versions (/pt/...) are their own canonical page
    const isTranslation = meta.i18n && this.$i18n.locale !== DEFAULT_LOCALE;
    const canonical = isTranslation ? pathUrl : resolve(meta.canonical) || pathUrl;
    // Pages that exist in every language point to each other (hreflang),
    // so search engines show each visitor the right language
    const alternates =
      meta.i18n && !meta.ownAlternates ? this.languageAlternates(route.path) : [];
    // Logged-in areas (admin, vendor, guide dashboards) stay out of search
    const isPrivate = meta.requiresAuth || meta.isAdmin || meta.isGuider;
    let robots =
      resolve(meta.robots) || (isPrivate ? "noindex, nofollow" : "index, follow");
    // Let Google show large image previews and full snippets
    if (robots === "index, follow") {
      robots += ", max-image-preview:large, max-snippet:-1";
    }
    const og = meta.og || {};
    const image = og.image || DEFAULT_IMAGE;

    return {
      title,
      htmlAttrs: { lang: this.$i18n.locale },
      meta: [
        { vmid: "description", name: "description", content: description },
        { vmid: "robots", name: "robots", content: robots },
        { vmid: "og:site_name", property: "og:site_name", content: SITE_NAME },
        { vmid: "og:type", property: "og:type", content: og.type || "website" },
        { vmid: "og:locale", property: "og:locale", content: currentLocaleInfo().ogLocale },
        { vmid: "og:title", property: "og:title", content: og.title || title },
        {
          vmid: "og:description",
          property: "og:description",
          content: og.description || description,
        },
        { vmid: "og:url", property: "og:url", content: og.url || canonical },
        { vmid: "og:image", property: "og:image", content: image },
        { vmid: "twitter:card", name: "twitter:card", content: "summary_large_image" },
        { vmid: "twitter:title", name: "twitter:title", content: og.title || title },
        {
          vmid: "twitter:description",
          name: "twitter:description",
          content: og.description || description,
        },
        { vmid: "twitter:image", name: "twitter:image", content: image },
      ],
      link: [{ vmid: "canonical", rel: "canonical", href: canonical }, ...alternates],
      script: [
        { vmid: "ld-organization", type: "application/ld+json", json: ORGANIZATION_LD },
      ],
    };
  },

  components: {
    navbar,
    Footer,
    VendorSidebar,
  },

  data() {
    return {
      loaded: false,
      isSignedIn: null,

      routesToHideFooter,
      routesToHideFooterAndNavBar,

      ResellerRoles,
    };
  },

  updated() {
    if (!this.user) {
      this.isSignedIn = true;
      return;
    }

    this.isSignedIn = false;
  },

  created() {
    if (!this.user) {
      this.isSignedIn = true;
    } else {
      this.isSignedIn = false;
    }

    AOS.init({
      disable: false,
      startEvent: "DOMContentLoaded",
      initClassName: "aos-init",
      animatedClassName: "aos-animate",
      useClassNames: false,
      disableMutationObserver: false,
      delay: 0,
      easing: "ease",
      once: false,
      mirror: false,
    });
  },

  async mounted() {
    // Pre-rendering waits for the page to be ready (see main.js)
    window.scrollTo(0, 0);
  },

  watch: {
    $route() {
      this.setLoaded(false);

      if (
        routesToHideFooter.includes(this.$route.name) ||
        routesToHideFooterAndNavBar.includes(this.$route.name)
      ) {
        this.setLoaded(true);
      }

      this.setLoaded(true);
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.users[UsersStates.USER],
    }),

    shouldShowVendorSidebar() {
      const routesToShowSidebar = [
        "vendor-dashboard",
        "generate-ticket",
      ].includes(this.$route.name);

      return (
        routesToShowSidebar &&
        !this.isMobileView
      );
    },

    // Every /admin page gets the same frame (see .admin-area below)
    isAdminArea() {
      return /^\/admin(\/|$)/.test(this.$route.path);
    },

    // Admin and Coreanos pages: Inter instead of the theme's Archivo
    isInterArea() {
      return /^\/(admin|coreanos|c)(\/|$)/.test(this.$route.path);
    },

    hideFooter() {
      return (
        routesToHideFooter.includes(this.$route.name) ||
        routesToHideFooterAndNavBar.includes(this.$route.name) ||
        // Back-office areas: /admin/..., /guide-portal/..., Coreanos (/coreanos/...)
        /^\/(admin|guide-portal|coreanos|c)(\/|$)/.test(this.$route.path)
      );
    },

    hideNavbar() {
      return routesToHideFooterAndNavBar.includes(
        this.$route.name
      );
    },
  },

  methods: {
    // <link rel="alternate" hreflang="..."> for every language, plus
    // x-default (English) for everyone else
    languageAlternates(path) {
      const base = splitLocalePath(path).path;
      const url = (code) => {
        const localized = localePath(base, code);
        return `${SITE_URL}${localized === "/" ? "/" : localized.replace(/\/+$/, "")}`;
      };
      return [
        ...LOCALES.map((l) => ({
          vmid: `alternate-${l.code}`,
          rel: "alternate",
          hreflang: l.code,
          href: url(l.code),
        })),
        { vmid: "alternate-x-default", rel: "alternate", hreflang: "x-default", href: url(DEFAULT_LOCALE) },
      ];
    },
    ...mapActions({
      authenticateUser: UserActions.AUTHENTICATE_USER,
    }),

    setLoaded(value) {
      this.loaded = value;
    },
  },
};
</script>

<style lang="scss">
@import "./styles/index";

@import url("https://fonts.googleapis.com/icon?family=Material+Icons");

@import url(
  "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500&family=Inter:wght@300;400;500;600;700&family=Roboto+Mono:400,500,700&display=swap"
);

/* --------------------------------
   Global
-------------------------------- */

body {
  margin: 0;
  color: $primary;
}

img {
  object-fit: cover;
}

/* --------------------------------
   App layout
-------------------------------- */

.app {
  font-family: "Inter", sans-serif;
  // Top: clears the fixed 72px header (components/elements/navbar.vue)
  padding: 84px 60px 60px;
  min-height: 100vh;
  color: $primary;
}

// Bulma sets the theme font (Archivo) on titles and form fields directly
.inter-font,
.inter-font :where(button, input, optgroup, select, textarea, .title, .subtitle, .page-title, .card-header-title) {
  font-family: "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
}

.app-page:not(.full-screen) {
  padding: 1rem 20px;
}

/* --------------------------------
   Admin pages (/admin/...): one frame for all of them, new ones included.
   Room for the fixed 72px navbar, the same side gutters and max width on
   every screen. The page's root element IS .app-page, so pages must not
   set their own outer margin/padding/max-width: just put the content in.
-------------------------------- */


.app-page {
  flex: 1;
  min-width: 0;
}

.app:has(.full-screen) {
  padding: 0;
  margin-bottom: 0;
}

// Full-screen pages (e.g. /concierge) still need room for the fixed 72px
// header when it's showing (the homepage-design pages have their own
// header, so .app-navbar isn't there)
.app:has(.app-navbar:not(.navbar--hidden)):has(.full-screen) {
  padding-top: 72px;
}

// Pop-ups (Buefy modals) above the fixed 72px header (z-index 1000), so
// their title bar and close button are never hidden under it
.modal {
  z-index: 1100;
}

// Screens that hide the top menu don't need room for it
.app:has(.navbar--hidden) {
  padding-top: 20px;
}

.app:has(.navbar--hidden):has(.full-screen) {
  padding-top: 0;
}

.app:has(.layout) {
  padding: 0;
}

.layout {
  display: flex;
}

.sidebar {
  flex: 0 0 330px;
  max-width: 330px;
  position: sticky;
  top: 0;
}

/* --------------------------------
   Navbar
-------------------------------- */

body.has-navbar-fixed-top:has(.navbar--hidden) {
  padding-top: 20px;
}

body.has-navbar-fixed-top:has(.navbar--hidden):has(.full-screen) {
  padding-top: 0;
}

.navbar-menu {
  background-color: white;
  box-shadow: none;
  padding: 0;
}

.navbar-menu {
  align-items: flex-start !important;
  padding: 20px 51px 8px 40px;
}

.navbar-item,
.navbar-link {
  font-size: 16px;
  padding: 0.5rem 0.7rem;
}

.navbar-brand,
.navbar-tabs {
  margin-left: 22px;
}

.navbar-burger:hover {
  background-color: transparent;
}

.navbar-burger span {
  height: 3px;
  color: $primary;
  width: 25px;
  border-radius: 2px;

  transform: rotate(0deg);
  transition: 0.5s ease-in-out;

  cursor: pointer;
}

.navbar-burger.is-active span:nth-child(3) {
  transform: translateY(-9px) rotate(316deg);
}

.navbar-burger span:nth-child(1) {
  top: calc(50% - 9px);
}

.navbar-burger span:nth-child(2) {
  top: calc(50% - 2px);
}

.navbar-burger span:nth-child(3) {
  top: calc(50% + 5px);
}

/* --------------------------------
   Forms
-------------------------------- */

.button {
  border-color: #edebf2;
}

.input:focus,
.textarea:focus,
.taginput .taginput-container.is-focusable:focus,
.select select:focus,
.is-focused.input,
.is-focused.textarea,
.taginput .is-focused.taginput-container.is-focusable,
.select select.is-focused,
.input:active,
.textarea:active,
.taginput .taginput-container.is-focusable:active,
.select select:active,
.is-active.input,
.is-active.textarea,
.taginput .is-active.taginput-container.is-focusable,
.select select.is-active {
  box-shadow: none;
  background-color: transparent;
  border-color: #e2e9ec;
}

.input:hover,
.textarea:hover,
.taginput .taginput-container.is-focusable:hover,
.select select:hover,
.is-hovered.input,
.is-hovered.textarea,
.taginput .is-hovered.taginput-container.is-focusable,
.select select.is-hovered {
  border-color: #e2e9ec;
  background-color: transparent;
}

.select select,
.taginput .taginput-container.is-focusable,
.textarea,
.input {
  border-color: #e2e9ec;
  background-color: transparent;
  box-shadow: none;
  border-radius: 5px;
  color: #171138a6;
}

/* --------------------------------
   Misc
-------------------------------- */

.trip-advisor {
  height: 3.5rem;
}

.stripe-badge--img {
  height: 2rem;
}

.stripe-badge {
  max-height: 9rem;
}

.icon--sm {
  height: 1rem;
}

.is-green {
  color: #61ce66;
}

.link {
  color: #2c2c2c;
}

.blue-container {
  padding: 8px 22px 0 22px;
  margin: 12px 0;
  background-color: rgb(249 251 251);
  color: #2c2c2c;
  border-radius: 22px;
  width: fit-content;
  font-weight: 600;
  display: flex;
}

.wavy-underline {
  text-decoration: underline wavy;
  text-decoration-color: #5995ed;
}

/* --------------------------------
   Scrollbar
-------------------------------- */

::-webkit-scrollbar {
  width: 20px;
}

::-webkit-scrollbar-track {
  background-color: transparent;
}

::-webkit-scrollbar-thumb {
  background-color: #2c2c2c;
  border-radius: 20px;
  border: 6px solid transparent;
  background-clip: content-box;
}

::-webkit-scrollbar-thumb:hover {
  background-color: #2c2c2c94;
}

/* --------------------------------
   Bulma / Buefy
-------------------------------- */

::v-deep {
  @import "~bulma/sass/utilities/_all";

  $primary: #8c67ef;
  $primary-light: findLightColor($primary);
  $primary-dark: findDarkColor($primary);
  $primary-invert: findColorInvert($primary);

  $modal-card-head-background-color: white;
  $modal-card-body-background-color: white !important;

  $link: $primary;
  $link-invert: $primary-invert;
  $link-focus-border: $primary;

  $colors: mergeColorMaps(
    (
      "primary": (
        $primary,
        $primary-invert,
        $primary-light,
        $primary-dark,
      ),
      "link": (
        $link,
        $link-invert,
        $link-light,
        $link-dark,
      ),
    ),
    $custom-colors
  );

  $checkbox-background-color: #f8f6ff;
  $checkbox-border-color: transparent;

  @import "~bulma";
  @import "~buefy/src/scss/buefy";
}

/* --------------------------------
   Loading overlay
-------------------------------- */

::v-deep .loading-overlay .loading-background {
  background: white;
}

/* --------------------------------
   Modals
-------------------------------- */

.modal-background {
  background-color: rgba(10, 10, 10, 0.75);
}

.delete::before,
.modal-close::before,
.delete::after,
.modal-close::after {
  background-color: $primary;
}

.delete::after,
.modal-close::after {
  height: 50%;
}

.delete::before,
.modal-close::before {
  width: 50%;
}

/* --------------------------------
   Datepicker
-------------------------------- */

.datepicker .dropdown .input[readonly],
.datepicker .dropdown-trigger .input[readonly] {
  cursor: pointer;
  box-shadow: none;
}

/* --------------------------------
   Mobile
-------------------------------- */

@media only screen and (max-width: 768px) {
  .navbar-menu {
    margin-bottom: 2px;
  }

  .navbar-brand,
  .navbar-tabs {
    margin: 0 20px;
  }

  .app {
    width: 100%;
    overflow: hidden;
    padding: 84px 20px 24px;
  }
}

@media screen and (max-width: 1023px) {
  .navbar.is-fixed-top .navbar-menu,
  .navbar.is-fixed-top-touch .navbar-menu {
    height: 100vh;
  }

  .navbar-menu {
    padding: 18px;
  }
}

/* --------------------------------
   Other
--------------------------------

div.container {
  max-width: 600px !important;
} */
</style>
